import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStytchMember, useStytchOrganization } from "@stytch/react/b2b";
import { usePermissionLevel } from "../hooks/usePermissionLevel";
import { navItemsFor } from "./navModel";
import MenuSurface, { MenuRow, MenuRule } from "./MenuSurface";

const initialsOf = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const LEVEL_LABEL: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  member: "Member",
};

const AvatarMenu: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { level } = usePermissionLevel();
  // Two hooks, not one: useStytchMember returns { member, fromCache,
  // isInitialized } and carries no organization at all.
  const { member } = useStytchMember();
  const { organization } = useStytchOrganization();

  const displayName = member?.name || member?.email_address || "Signed in";
  const orgName = organization?.organization_name ?? "";
  const items = navItemsFor(level);

  return (
    <MenuSurface
      open={open}
      onOpenChange={setOpen}
      align="right"
      triggerTitle="Account"
      triggerClassName="flex items-center gap-1.5 p-1 pr-1.5 rounded-full hover:bg-surface-container transition-colors"
      trigger={
        <>
          <span className="grid place-items-center w-7 h-7 rounded-full bg-surface-container-highest text-on-surface font-label text-[10px] font-bold tracking-wide">
            {initialsOf(displayName)}
          </span>
          <span className="material-symbols-outlined text-[16px] text-slate-500">
            expand_more
          </span>
        </>
      }
    >
      <div className="px-2.5 pt-2 pb-1.5 flex flex-col gap-0.5">
        <span className="font-label text-[13px] font-semibold text-on-surface truncate">
          {displayName}
        </span>
        <span className="font-label text-[11px] text-slate-500 truncate">
          {orgName && `${orgName} · `}
          <span className={level === "member" ? "text-slate-500" : "text-tertiary"}>
            {LEVEL_LABEL[level]}
          </span>
        </span>
      </div>

      {/* A member has no destinations here, so the rule would introduce a
          divider with nothing under it. */}
      {items.length > 0 && (
        <>
          <MenuRule />
          {items.map((item) => (
            <MenuRow
              key={item.path}
              icon={item.icon}
              onClick={() => {
                setOpen(false);
                navigate(`/${orgSlug}/${item.path}`);
              }}
            >
              {item.label}
            </MenuRow>
          ))}
        </>
      )}

      <MenuRule />
      <MenuRow
        icon="logout"
        danger
        onClick={() => {
          setOpen(false);
          onLogout();
        }}
      >
        Log out
      </MenuRow>
    </MenuSurface>
  );
};

export default AvatarMenu;
